/** Locale-safe formatting (frontend-architecture §8). CHF with apostrophe thousands (K6). */

/** Absolute value, grouped with apostrophe thousands + two decimals: "52'501.20". */
function groupedAbs(v: number): string {
  const [int, dec] = Math.abs(v).toFixed(2).split('.');
  return `${int.replace(/\B(?=(\d{3})+(?!\d))/g, "'")}.${dec}`;
}

/** Grouped exact amount without the "CHF" prefix ("52'501.20"). */
export function amount(v: number): string {
  return `${v < 0 ? '-' : ''}${groupedAbs(v)}`;
}

export function chf(v: number): string {
  return `${v < 0 ? '-' : ''}CHF ${groupedAbs(v)}`;
}

/** Unit suffixes for the compact formatters — change here to switch to "k"/"M". */
export const COMPACT_UNITS = { thousand: 'Tsd.', million: 'Mio.' } as const;

/** Absolute compact amount ("52.5 Tsd." / "1.2 Mio." / exact "9'999.99"), no currency prefix. */
function compactAbs(a: number): string {
  // ≥ 999'950 rounds up to "1.0 Mio." rather than "1000.0 Tsd.".
  if (a >= 999_950) return `${(a / 1_000_000).toFixed(1)} ${COMPACT_UNITS.million}`;
  if (a >= 10_000) return `${(a / 1_000).toFixed(1)} ${COMPACT_UNITS.thousand}`;
  return groupedAbs(a);
}

/** Compact amount without the "CHF" prefix ("52.5 Tsd." / "1.2 Mio."; exact below 10'000). */
export function amountCompact(v: number): string {
  return `${v < 0 ? '-' : ''}${compactAbs(Math.abs(v))}`;
}

/** Compact amount with the "CHF" prefix ("CHF 52.5 Tsd."). */
export function chfCompact(v: number): string {
  return `${v < 0 ? '-' : ''}CHF ${compactAbs(Math.abs(v))}`;
}

export function pct(v: number): string {
  return `${v.toFixed(1).replace('.', '.')}%`;
}

/** ISO timestamp → 'dd.MM.yyyy' (de-CH). Empty string on invalid input. (Story V10-J) */
export function dateShortDe(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/**
 * ISO date (yyyy-mm-dd) → 'MM/YYYY' (e.g. '2026-03-15' → '03/2026'). Empty string on invalid input.
 * The caller renders '–' for a null value (P2 Placement Slice 7 — the Versicherer-Tile Platzierung line).
 */
export function monthYearDe(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${mm}/${d.getFullYear()}`;
}

/**
 * ISO timestamp → long weekday + date (de-CH), e.g. 'Donnerstag, 26. Juni 2026'.
 * Empty string on invalid input. Used by the Heute greeting stamp (Story UI-U3).
 * "No ad-hoc toLocaleString in components" — the greeting line goes through here.
 */
export function dateLongDe(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('de-CH', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

/** ISO timestamp → 'HH:mm' (de-CH). Empty string on invalid input. (Story UI-U3) */
export function timeShortDe(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
}

/** ISO timestamp → 'dd.MM.yyyy, HH:mm' (de-CH). Empty string on invalid input. (Story V10-J) */
export function dateTimeShortDe(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const date = d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const time = d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  return `${date}, ${time}`;
}

/** ISO → "Di 29.09., 22:45" (weekday, date, time); '' on missing or invalid input. */
export function weekdayDateTimeDe(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const weekday = d.toLocaleDateString('de-CH', { weekday: 'short' }).replace(/\.$/, '');
  const date = d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit' });
  const time = d.toLocaleTimeString('de-CH', { hour: '2-digit', minute: '2-digit' });
  return `${weekday} ${date.replace(/\.$/, '')}., ${time}`;
}

/**
 * ISO date → whole days from today (local midnight-to-midnight; negative = past).
 * Null on invalid/missing input. Display helper only (Story V10-J2) — the UI
 * renders remaining days; no domain rule is computed from this.
 */
export function daysUntil(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  const a = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const b = new Date(today.getFullYear(), today.getMonth(), today.getDate()).getTime();
  return Math.round((a - b) / 86_400_000);
}

/** Bytes → human-readable size ('12 KB', '1.4 MB'). Empty string when null. (Story V10-J) */
export function fileSize(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
